// Domain types split out of the former monolithic `services/interface.ts` (MDCL-16193).
// Import via the `services/interface` barrel; this module is a domain slice, not a public entry point.
import type { ComponentProps } from 'react';
import { Icon } from '@opswat/ui';
import type {
    SandboxData,
    SandboxReport,
    SandboxResources,
} from './sandbox';
import type { FileResponseInterface, SandboxResponseInterface } from './fileResults';

export enum FileDetailsTabs {
    Overview = 'overview',
    ThreatsPrevented = 'threats-prevented',
    AdvancedAnalysis = 'advanced-analysis',
    DataLossPrevented = 'data-loss-prevented',
    Sanitization = 'sanitization',
    Feedback = 'feedback',
    AiContentInspector = 'ai-content-inspector',
}

export interface AigcdDetectorMetric {
    display_name?: string;
    value?: number | string | boolean;
}

export interface AigcdDetector {
    display_name?: string;
    confidence?: number;
    metrics?: Record<string, AigcdDetectorMetric>;
    output_images?: Record<
        string,
        { display_name?: string; file_name?: string; size?: { width?: number; height?: number } }
    >;
}

export interface AigcdFeatureResult {
    display_name?: string;
    result?: string;
    ai_probability?: number;
    input_image_size?: { width?: number; height?: number };
    detectors?: Record<string, AigcdDetector>;
    details?: {
        document_type?: string;
        document_type_display_name?: string;
        document_type_confidence?: number;
        ai_probability?: number;
    };
}

export interface AigcdOutputImage {
    output_file_name?: string;
    image_alias?: string;
    file_url?: string;
}

export interface AigcdInfo {
    // err_code 6 === "Unsupported File Type"; final_verdict is absent in that case.
    err_code?: number;
    err_details?: string;
    final_verdict?: {
        verdict?: string;
        verdict_explanation?: string[];
    };
    result_description?: {
        feature_results?: Record<string, AigcdFeatureResult>;
        detection_details?: Record<string, { threshold?: number; suspicious_threshold?: number }>;
    };
    output_images?: AigcdOutputImage[];
    result_template_hash?: string;
    retried?: number;
}

export enum AdvancedAnalysisTabs {
    FileDetails = 'file-details',
    EmulationGraph = 'emulation-graph',
    YaraRules = 'yara-rules',
    ExtractedFiles = 'extracted-files',
    ExtractedStrings = 'extracted-strings',
    OsintLookups = 'osint-lookups',
}

export enum FileDetailsSandboxTabs {
    Meta = 'meta',
    OLEStreams = 'ole-streams',
    Exports = 'exports',
    Certificates = 'certificates',
    Resources = 'resources',
    Sections = 'sections',
    Verinfo = 'verinfo',
    RichHeader = 'rich header',
    Header = 'header',
    Characteristics = 'characteristics',
    PdbData = 'pdb-data',
    Imports = 'imports',
}

// Interfaces moved from Overview.tsx
export interface OverviewFileInfo {
    display_name?: string;
    file_size?: number;
    file_type_category?: string;
    file_type_description?: string;
    file_type_extension?: string;
    md5?: string;
    sha1?: string;
    sha256?: string;
    upload_timestamp?: string;
}

export interface MetaData {
    entropy?: number;
}

export interface VerInfo {
    CompanyName?: string;
    FileDescription?: string;
    FileVersion?: string;
    InternalName?: string;
    LegalCopyright?: string;
    ProductName?: string;
    ProductVersion?: string;
    name?: string;
    value?: string;
    map?: (callback: (item: unknown) => unknown) => unknown[];
}

export interface OverviewProps {
    fileData?: FileResponseInterface;
    sandboxResources?: SandboxResources;
    fileDataError?: boolean;
    sandboxReport?: SandboxReport;
    sandboxData?: SandboxResponseInterface;
    sandboxError?: boolean;
    sandboxId?: string;
    filescanVerdict?: string;
    filescanVerdictAlert?: boolean;
}

export interface GeoData {
    country: {
        name: string;
    };
    location: {
        latitude: number;
        longitude: number;
    };
}

export interface IndicatorItemProps {
    icon: ComponentProps<typeof Icon>['iconName'];
    label: string;
    count: number;
    variant?: 'malicious' | 'likelyMalicious' | 'suspicious' | 'other' | 'neutral';
    className?: string;
}

export interface ThreatsPreventedProps {
    fileData?: FileResponseInterface;
    sandboxReport?: SandboxData;
    sandboxResources?: SandboxResources;
    sandboxError?: boolean;
    sandboxId?: string;
    sandboxData?: SandboxResponseInterface;
}

export interface CVEsInterface {
    updated_ts: string;
    severity: string;
    description: string;
    published_epoch: number;
    last_modified_epoch: number;
    hashes_count: number;
    sha256: string[];
    cvss_2_0: {
        score: number;
        authentication: string;
        integrity_impact: string;
        confidentiality_impact: string;
        availability_impact: string;
        access_vector: string;
        access_complexity: string;
        attack_complexity: string;
    };
    cvss_3_0: {
        base_score: number;
        base_severity: string;
        vector_string: string;
        attack_vector: string;
        integrity_impact: string;
        confidentiality_impact: string;
        availability_impact: string;
        attack_complexity: string;
        authentication: string;
        privileges_required: string;
        user_interaction: string;
        scope: string;
    };
    references: Array<{
        url: string;
        name: string;
    }>;
    opswat_product_info: Array<{
        vendor: { name: string };
    }>;
    resolution: Array<{
        product_name: string;
        higher_than_version: string;
        higher_than_or_equal_to_version: string;
    }>;
    product_info?: {
        product?: {
            name: string;
        };
    };
    cve: string;
    severity_index: string;
}

export interface VulnerabilityResponseInterface {
    cves: string[];
    vulnerability: {
        severity: string;
        severity_index: number;
    };
}

export interface VulnerabilityDataInterface {
    data: Array<CVEsInterface>;
}
